import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Warning, ArrowClockwise, CheckCircle, LinkBreak } from "@phosphor-icons/react";
import api from "../lib/api";

const COLLECTION_LABEL = {
  sales_orders: "Sales Documents",
  purchase_orders: "Purchase Documents",
  items: "Items",
  projects: "Projects",
};

const CUSTOMER_REFS = ["customer", "project_customer"];
const VENDOR_REFS = ["vendor", "item_vendor"];

function IssueRow({ issue, customers, vendors, onFix, busy }) {
  const [target, setTarget] = useState("");
  const isCustomer = CUSTOMER_REFS.includes(issue.ref_type);
  const isVendor = VENDOR_REFS.includes(issue.ref_type);
  const isLine = issue.ref_type === "line_item";
  const options = isCustomer ? customers : isVendor ? vendors : [];

  return (
    <tr className="border-b border-border/60 align-top" data-testid="integrity-issue-row">
      <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">{COLLECTION_LABEL[issue.collection] || issue.collection}</td>
      <td className="py-3 px-4 font-mono text-xs font-semibold whitespace-nowrap">{issue.doc_no}</td>
      <td className="py-3 px-4">
        <span className="inline-flex items-center gap-1.5">
          <Warning size={14} weight="fill" className="text-amber-500 shrink-0" />
          {issue.issue}
        </span>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {(isCustomer || isVendor) && (
            <>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                disabled={busy}
                data-testid="integrity-reassign-select"
                className="h-8 rounded-sm border border-border bg-background text-xs px-2 min-w-[150px] max-w-[180px]"
              >
                <option value="">Reassign to…</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <button
                onClick={() => onFix(issue, "reassign", target)}
                disabled={busy || !target}
                data-testid="integrity-reassign-btn"
                className="h-8 px-3 rounded-sm bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
              >
                Reassign
              </button>
              <button
                onClick={() => onFix(issue, "clear", "")}
                disabled={busy}
                data-testid="integrity-clear-btn"
                className="h-8 px-3 rounded-sm border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50"
              >
                Clear link
              </button>
            </>
          )}
          {isLine && (
            <button
              onClick={() => onFix(issue, "detach", "")}
              disabled={busy}
              data-testid="integrity-detach-btn"
              className="h-8 px-3 rounded-sm border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <LinkBreak size={13} weight="bold" /> Detach item
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function DataIntegrity() {
  const qc = useQueryClient();
  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: ["data-integrity"],
    queryFn: async () => (await api.get("/admin/integrity")).data,
    refetchOnWindowFocus: false,
  });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });

  const [busy, setBusy] = useState(false);
  const fixMutation = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/integrity/fix", payload)).data,
    onMutate: () => setBusy(true),
    onSuccess: () => { toast.success("Link repaired"); qc.invalidateQueries({ queryKey: ["data-integrity"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not repair this link"),
    onSettled: () => setBusy(false),
  });

  const onFix = (issue, action, newRefId) => {
    fixMutation.mutate({
      collection: issue.collection,
      doc_id: issue.doc_id,
      ref_type: issue.ref_type,
      action,
      new_ref_id: newRefId || "",
      line_item_id: issue.line_item_id || "",
    });
  };

  const issues = data?.issues || [];
  const byCollection = data?.by_collection || {};

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="data-integrity-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight mt-1">Data Integrity</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Scans your records for broken links — documents pointing at a customer, vendor or item that has since been deleted.
            Repair each one by reassigning it, clearing the link, or detaching the missing item.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="integrity-run-scan"
          className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          <ArrowClockwise size={15} weight="bold" className={isFetching ? "animate-spin" : ""} />
          {isFetching ? "Scanning…" : "Run scan"}
        </button>
      </div>

      {isError && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-4 text-sm text-destructive" data-testid="integrity-error">
          The scan could not be completed. Please try again.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="integrity-summary">
            <div className="border border-border rounded-sm p-4 bg-card">
              <p className="overline-label">Total issues</p>
              <p className={`text-3xl font-black mt-1 ${data.total_issues ? "text-destructive" : "text-emerald-600"}`} data-testid="integrity-total">{data.total_issues}</p>
            </div>
            <div className="border border-border rounded-sm p-4 bg-card">
              <p className="overline-label">Customers</p>
              <p className="text-3xl font-black mt-1">{data.scanned?.customers ?? 0}</p>
            </div>
            <div className="border border-border rounded-sm p-4 bg-card">
              <p className="overline-label">Vendors</p>
              <p className="text-3xl font-black mt-1">{data.scanned?.vendors ?? 0}</p>
            </div>
            <div className="border border-border rounded-sm p-4 bg-card">
              <p className="overline-label">Items</p>
              <p className="text-3xl font-black mt-1">{data.scanned?.items ?? 0}</p>
            </div>
          </div>

          {data.total_issues === 0 ? (
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-8 text-center" data-testid="integrity-all-clear">
              <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto mb-3" />
              <p className="font-bold">All links are healthy</p>
              <p className="text-sm text-muted-foreground mt-1">No orphaned references were found across your documents and master data.</p>
            </div>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="integrity-issues-table">
              <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-border bg-secondary/40">
                {Object.entries(byCollection).map(([c, n]) => (
                  <span key={c} className="text-xs font-semibold px-2 py-1 rounded-sm bg-destructive/10 text-destructive">
                    {COLLECTION_LABEL[c] || c}: {n}
                  </span>
                ))}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Area</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Record</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Problem</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold text-right">Repair</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((i, idx) => (
                    <IssueRow key={`${i.doc_id}-${i.ref_type}-${i.line_item_id}-${idx}`} issue={i} customers={customers} vendors={vendors} onFix={onFix} busy={busy} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
